import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Dashboard
  getDashboardStats: () => axios.get(`${API}/dashboard/stats`, { headers: getAuthHeader() }),
  
  // Portfolios
  getPortfolios: () => axios.get(`${API}/portfolios`, { headers: getAuthHeader() }),
  createPortfolio: (data) => axios.post(`${API}/portfolios`, data, { headers: getAuthHeader() }),
  updatePortfolio: (id, data) => axios.put(`${API}/portfolios/${id}`, data, { headers: getAuthHeader() }),
  deletePortfolio: (id) => axios.delete(`${API}/portfolios/${id}`, { headers: getAuthHeader() }),
  
  // Properties
  getProperties: (portfolioId) => {
    const url = portfolioId ? `${API}/properties?portfolio_id=${portfolioId}` : `${API}/properties`;
    return axios.get(url, { headers: getAuthHeader() });
  },
  getPropertiesPaginated: (params = {}) => {
    const q = new URLSearchParams();
    if (params.page) q.set('page', params.page);
    if (params.page_size) q.set('page_size', params.page_size);
    if (params.search) q.set('search', params.search);
    if (params.sort_by) q.set('sort_by', params.sort_by);
    if (params.sort_dir) q.set('sort_dir', params.sort_dir);
    if (params.filters) Object.entries(params.filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    return axios.get(`${API}/properties/paginated?${q.toString()}`, { headers: getAuthHeader() });
  },
  createProperty: (data) => axios.post(`${API}/properties`, data, { headers: getAuthHeader() }),
  updateProperty: (id, data) => axios.put(`${API}/properties/${id}`, data, { headers: getAuthHeader() }),
  deleteProperty: (id) => axios.delete(`${API}/properties/${id}`, { headers: getAuthHeader() }),
  
  // Units
  getUnits: (propertyId) => {
    const url = propertyId ? `${API}/units?property_id=${propertyId}` : `${API}/units`;
    return axios.get(url, { headers: getAuthHeader() });
  },
  createUnit: (data) => axios.post(`${API}/units`, data, { headers: getAuthHeader() }),
  updateUnit: (id, data) => axios.put(`${API}/units/${id}`, data, { headers: getAuthHeader() }),
  deleteUnit: (id) => axios.delete(`${API}/units/${id}`, { headers: getAuthHeader() }),
  
  // Tenants
  getTenants: () => axios.get(`${API}/tenants`, { headers: getAuthHeader() }),
  createTenant: (data) => axios.post(`${API}/tenants`, data, { headers: getAuthHeader() }),
  updateTenant: (id, data) => axios.put(`${API}/tenants/${id}`, data, { headers: getAuthHeader() }),
  deleteTenant: (id) => axios.delete(`${API}/tenants/${id}`, { headers: getAuthHeader() }),
  
  // Leases
  getLeases: (filters) => {
    let url = `${API}/leases`;
    const params = new URLSearchParams();
    if (filters?.unit_id) params.append('unit_id', filters.unit_id);
    if (filters?.tenant_id) params.append('tenant_id', filters.tenant_id);
    if (params.toString()) url += `?${params.toString()}`;
    return axios.get(url, { headers: getAuthHeader() });
  },
  createLease: (data) => axios.post(`${API}/leases`, data, { headers: getAuthHeader() }),
  updateLease: (id, data) => axios.put(`${API}/leases/${id}`, data, { headers: getAuthHeader() }),
  
  // Payments
  getPayments: (filters) => {
    let url = `${API}/payments`;
    const params = new URLSearchParams();
    if (filters?.lease_id) params.append('lease_id', filters.lease_id);
    if (filters?.status) params.append('status', filters.status);
    if (params.toString()) url += `?${params.toString()}`;
    return axios.get(url, { headers: getAuthHeader() });
  },
  createPayment: (data) => axios.post(`${API}/payments`, data, { headers: getAuthHeader() }),
  markPaymentPaid: (id, amount, method) => 
    axios.put(`${API}/payments/${id}/mark-paid?paid_amount=${amount}&payment_method=${method}`, {}, { headers: getAuthHeader() }),
  
  // Maintenance
  getMaintenanceRequests: (filters) => {
    let url = `${API}/maintenance`;
    const params = new URLSearchParams();
    if (filters?.unit_id) params.append('unit_id', filters.unit_id);
    if (filters?.status) params.append('status', filters.status);
    if (params.toString()) url += `?${params.toString()}`;
    return axios.get(url, { headers: getAuthHeader() });
  },
  createMaintenanceRequest: (data) => axios.post(`${API}/maintenance`, data, { headers: getAuthHeader() }),
  assignMaintenance: (id, vendorId) => 
    axios.put(`${API}/maintenance/${id}/assign?vendor_id=${vendorId}`, {}, { headers: getAuthHeader() }),
  completeMaintenance: (id, cost) => 
    axios.put(`${API}/maintenance/${id}/complete?actual_cost=${cost}`, {}, { headers: getAuthHeader() }),
  
  // Vendors
  getVendors: () => axios.get(`${API}/vendors`, { headers: getAuthHeader() }),
  createVendor: (data) => axios.post(`${API}/vendors`, data, { headers: getAuthHeader() }),
  
  // Revenue Insights
  getRevenueInsights: () => axios.get(`${API}/insights/revenue`, { headers: getAuthHeader() }),
  
  // Community
  getCommunityPosts: () => axios.get(`${API}/community/posts`, { headers: getAuthHeader() }),
  createCommunityPost: (data) => axios.post(`${API}/community/posts`, data, { headers: getAuthHeader() }),

  // Decision Center — Revenue Validation Track
  getPortfolioHealth: () => axios.get(`${API}/decisions/portfolio-health`, { headers: getAuthHeader() }),
  getPropertyHealthList: () => axios.get(`${API}/decisions/property-health`, { headers: getAuthHeader() }),
  getPropertyHealth: (id) => axios.get(`${API}/decisions/property-health/${id}`, { headers: getAuthHeader() }),
  getRoiOpportunities: () => axios.get(`${API}/decisions/roi-opportunities`, { headers: getAuthHeader() }),
  getDecisionRecommendations: (limit = 8) => axios.get(`${API}/decisions/recommendations?limit=${limit}`, { headers: getAuthHeader() }),
  postRootCause: (metric, period_days = 30) => axios.post(`${API}/decisions/root-cause`, { metric, period_days }, { headers: getAuthHeader() }),
  postDecisionsAsk: (question, language = 'en', scope = null) => axios.post(`${API}/decisions/ask`, { question, language, scope }, { headers: getAuthHeader() }),
  getModuleIntelligence: (module) => axios.get(`${API}/decisions/module-intelligence/${module}`, { headers: getAuthHeader() }),

  // Seed Data
  seedDemoData: () => axios.post(`${API}/seed/demo`, {}, { headers: getAuthHeader() }),
  seedDemoPortfolio: () => axios.post(`${API}/seed/demo-portfolio`, {}, { headers: getAuthHeader() }),
  resetDemoPortfolio: () => axios.post(`${API}/seed/demo-portfolio/reset`, {}, { headers: getAuthHeader() }),
  seedAllRoles: () => axios.post(`${API}/seed/all-roles`, {}, { headers: getAuthHeader() }),

  // Reports
  downloadPDF: () => axios.get(`${API}/reports/financial/pdf`, { headers: getAuthHeader(), responseType: 'blob' }),
  downloadExcel: () => axios.get(`${API}/reports/financial/excel`, { headers: getAuthHeader(), responseType: 'blob' }),

  // Notifications
  checkAlerts: () => axios.post(`${API}/notifications/check-alerts`, {}, { headers: getAuthHeader() }),
  getNotifications: () => axios.get(`${API}/notifications`, { headers: getAuthHeader() }),
  getNotificationCount: () => axios.get(`${API}/notifications/count`, { headers: getAuthHeader() }),
  markNotificationRead: (id) => axios.put(`${API}/notifications/${id}/read`, {}, { headers: getAuthHeader() }),
  markAllNotificationsRead: () => axios.put(`${API}/notifications/read-all`, {}, { headers: getAuthHeader() }),

  // AI Assistant — operational copilot grounded in live portfolio context
  aiAssistantChat: (messages, language = 'en', session_id = null) =>
    axios.post(`${API}/ai/assistant/chat`, { messages, language, session_id }, { headers: getAuthHeader() }),

  // Operational intelligence engine
  intelRecommendations:   () => axios.get(`${API}/intel/recommendations`,   { headers: getAuthHeader() }),
  intelVacancyForecast:   () => axios.get(`${API}/intel/vacancy-forecast`,   { headers: getAuthHeader() }),
  intelNoiForecast:       () => axios.get(`${API}/intel/noi-forecast`,       { headers: getAuthHeader() }),
  intelTenantRisk:        () => axios.get(`${API}/intel/tenant-risk`,        { headers: getAuthHeader() }),
  intelRenewalPipeline:   () => axios.get(`${API}/intel/renewal-pipeline`,   { headers: getAuthHeader() }),
  intelRevenueLeak:       () => axios.get(`${API}/intel/revenue-leak`,       { headers: getAuthHeader() }),

  // Action Center
  createAction:    (data) => axios.post(`${API}/actions`, data, { headers: getAuthHeader() }),
  listActions:     (status = null) => axios.get(`${API}/actions${status ? `?status=${status}` : ''}`, { headers: getAuthHeader() }),
  getAction:       (id) => axios.get(`${API}/actions/${id}`, { headers: getAuthHeader() }),
  updateAction:    (id, status) => axios.put(`${API}/actions/${id}`, { status }, { headers: getAuthHeader() }),
  // Phase D — workflow
  approveAction:   (id, decision, note = null) => axios.post(`${API}/actions/${id}/approve`, { decision, note }, { headers: getAuthHeader() }),
  getActionAudit:  (id) => axios.get(`${API}/actions/${id}/audit`, { headers: getAuthHeader() }),
  escalateAction:  (id) => axios.post(`${API}/actions/${id}/escalate`, {}, { headers: getAuthHeader() }),
  createLeaseRenewal: (data) => axios.post(`${API}/actions/lease-renewal`, data, { headers: getAuthHeader() }),

  // Tenant Portal
  getTenantDashboard: () => axios.get(`${API}/tenant/dashboard`, { headers: getAuthHeader() }),
  tenantSubmitMaintenance: (data) => axios.post(`${API}/tenant/maintenance`, data, { headers: getAuthHeader() }),

  // Stripe Payments
  createStripeCheckout: (paymentId) => axios.post(`${API}/stripe/checkout`, {
    payment_id: paymentId,
    origin_url: window.location.origin
  }, { headers: getAuthHeader() }),
  getStripeStatus: (sessionId) => axios.get(`${API}/stripe/status/${sessionId}`, { headers: getAuthHeader() }),

  // Subscriptions
  getSubscriptionPlans: () => axios.get(`${API}/subscription/plans`, { headers: getAuthHeader() }),
  getCurrentSubscription: () => axios.get(`${API}/subscription/current`, { headers: getAuthHeader() }),
  createSubscriptionCheckout: (planId) => axios.post(`${API}/subscription/checkout`, {
    plan_id: planId,
    origin_url: window.location.origin
  }, { headers: getAuthHeader() }),
  getSubscriptionStatus: (sessionId) => axios.get(`${API}/subscription/status/${sessionId}`, { headers: getAuthHeader() }),

  // AI
  aiMarketIntelligence: (location, propertyType) => axios.post(`${API}/ai/market-intelligence`, { location, property_type: propertyType }, { headers: getAuthHeader() }),
  aiValuation: (data) => axios.post(`${API}/ai/valuation`, data, { headers: getAuthHeader() }),
  aiInvestmentAnalysis: (data) => axios.post(`${API}/ai/investment-analysis`, data, { headers: getAuthHeader() }),
  aiExecutiveReport: () => axios.get(`${API}/ai/executive-report`, { headers: getAuthHeader() }),

  // Feature Access
  getFeatureAccess: () => axios.get(`${API}/features/access`, { headers: getAuthHeader() }),

  // Documents
  getDocuments: (relatedTo, docType) => {
    let url = `${API}/documents`;
    const params = new URLSearchParams();
    if (relatedTo) params.append('related_to', relatedTo);
    if (docType) params.append('document_type', docType);
    if (params.toString()) url += `?${params.toString()}`;
    return axios.get(url, { headers: getAuthHeader() });
  },
  createDocument: (data) => axios.post(`${API}/documents`, data, { headers: getAuthHeader() }),
  deleteDocument: (id) => axios.delete(`${API}/documents/${id}`, { headers: getAuthHeader() }),

  // Inspections
  getInspections: (propertyId) => {
    let url = `${API}/inspections`;
    if (propertyId) url += `?property_id=${propertyId}`;
    return axios.get(url, { headers: getAuthHeader() });
  },
  createInspection: (data) => axios.post(`${API}/inspections`, data, { headers: getAuthHeader() }),
  aiAnalyzeInspection: (id) => axios.post(`${API}/inspections/${id}/ai-analyze`, {}, { headers: getAuthHeader() }),

  // Digital Twin
  getDigitalTwinBuildings: () => axios.get(`${API}/digital-twin/buildings`, { headers: getAuthHeader() }),
  simulateMaintenance: (buildingId) => axios.post(`${API}/digital-twin/${buildingId}/simulate`, {}, { headers: getAuthHeader() }),

  // Role Dashboard
  getRoleDashboard: () => axios.get(`${API}/dashboard/role-view`, { headers: getAuthHeader() }),

  // Demo / Public
  submitDemoRequest: (data) => axios.post(`${API}/demo-request`, data),
  ensureDemoAccount: () => axios.post(`${API}/auth/ensure-demo-account`),

  // Document upload (file)
  uploadDocument: (formData) => axios.post(`${API}/documents/upload`, formData, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    onUploadProgress: undefined
  }),

  // Audit log
  getAuditLog: () => axios.get(`${API}/audit-log`, { headers: getAuthHeader() }),

  // CRM Requests Center
  listDemoRequests: (status) => axios.get(`${API}/demo-requests${status && status !== 'all' ? `?status=${status}` : ''}`, { headers: getAuthHeader() }),
  updateDemoRequest: (id, data) => axios.put(`${API}/demo-requests/${id}`, data, { headers: getAuthHeader() }),
  deleteDemoRequest: (id) => axios.delete(`${API}/demo-requests/${id}`, { headers: getAuthHeader() }),
  addCRMNote:        (id, text) => axios.post(`${API}/demo-requests/${id}/notes`, { text }, { headers: getAuthHeader() }),
  convertCRMToTask:  (id, data = {}) => axios.post(`${API}/demo-requests/${id}/convert-to-task`, data, { headers: getAuthHeader() }),

  // Compliance — PDPL data export + TOTP 2FA
  exportMyData:  () => axios.get(`${API}/me/export-data`, { headers: getAuthHeader(), responseType: 'blob' }),
  twoFAStatus:   () => axios.get(`${API}/me/2fa/status`, { headers: getAuthHeader() }),
  twoFASetup:    () => axios.post(`${API}/me/2fa/setup`, {}, { headers: getAuthHeader() }),
  twoFAEnable:   (code) => axios.post(`${API}/me/2fa/enable`, { code }, { headers: getAuthHeader() }),
  twoFADisable:  (code) => axios.post(`${API}/me/2fa/disable`, { code }, { headers: getAuthHeader() }),

  // Team / RBAC
  getMyPermissions: () => axios.get(`${API}/me/permissions`, { headers: getAuthHeader() }),
  listTeamUsers: () => axios.get(`${API}/team/users`, { headers: getAuthHeader() }),
  updateTeamUser: (id, data) => axios.put(`${API}/team/users/${id}`, data, { headers: getAuthHeader() }),
  listInvitations: () => axios.get(`${API}/team/invitations`, { headers: getAuthHeader() }),
  createInvitation: (data) => axios.post(`${API}/team/invite`, data, { headers: getAuthHeader() }),
  revokeInvitation: (id) => axios.delete(`${API}/team/invitations/${id}`, { headers: getAuthHeader() }),
  getInviteInfo: (token) => axios.get(`${API}/auth/invite-info/${token}`),
  acceptInvite: (data) => axios.post(`${API}/auth/accept-invite`, data),

  // Excel multi-sheet import
  downloadExcelTemplate: () => axios.get(`${API}/bulk-import/template`, { headers: getAuthHeader(), responseType: 'blob' }),
  excelImportPreview: (formData) => axios.post(`${API}/bulk-import/preview`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }),
  excelImportConfirm: (uploadId) => {
    const fd = new FormData(); fd.append('upload_id', uploadId);
    return axios.post(`${API}/bulk-import/confirm`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
  },
  excelImportHistory: () => axios.get(`${API}/bulk-import/history`, { headers: getAuthHeader() }),

  // AI-assisted Excel column mapping (the "messy file" onboarding moat)
  aiDetectSchema: (formData) => axios.post(`${API}/bulk-import/ai-detect-schema`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }),
  confirmMapping: (uploadId, mappings) => axios.post(`${API}/bulk-import/confirm-mapping`, { upload_id: uploadId, mappings }, { headers: getAuthHeader() }),

  // AI Video Generator (HeyGen)
  generateVideo: (data) => axios.post(`${API}/videos/generate`, data, { headers: getAuthHeader() }),
  getVideoStatus: (id) => axios.get(`${API}/videos/${id}/status`, { headers: getAuthHeader() }),
  listVideos: () => axios.get(`${API}/videos`, { headers: getAuthHeader() }),
  deleteVideo: (id) => axios.delete(`${API}/videos/${id}`, { headers: getAuthHeader() }),

  // ── RBAC v2 ──
  rbacListUsers: () => axios.get(`${API}/rbac/users`, { headers: getAuthHeader() }),
  rbacSetUserPermissions: (userId, body) => axios.post(`${API}/rbac/users/${userId}/permissions`, body, { headers: getAuthHeader() }),
  rbacApplyTemplate: (userId, body) => axios.post(`${API}/rbac/users/${userId}/template`, body, { headers: getAuthHeader() }),
  rbacRemoveUser: (userId) => axios.delete(`${API}/rbac/users/${userId}`, { headers: getAuthHeader() }),
  rbacTemplates: () => axios.get(`${API}/rbac/templates`, { headers: getAuthHeader() }),
  rbacMeEffective: () => axios.get(`${API}/rbac/me/effective`, { headers: getAuthHeader() }),

  // ── Governance · Safe-delete ──
  governanceSoftDelete: (entityType, entityId, reason) => axios.post(`${API}/governance/soft-delete/${entityType}/${entityId}`, { reason }, { headers: getAuthHeader() }),
  governanceListRequests: (status) => axios.get(`${API}/governance/deletion-requests${status ? `?status=${status}` : ''}`, { headers: getAuthHeader() }),
  governanceRestore: (requestId, note) => axios.post(`${API}/governance/deletion-requests/${requestId}/restore`, { note }, { headers: getAuthHeader() }),
  governancePurge: (requestId, note) => axios.post(`${API}/governance/deletion-requests/${requestId}/purge`, { note }, { headers: getAuthHeader() }),
  governanceTrash: () => axios.get(`${API}/governance/trash`, { headers: getAuthHeader() }),
  governanceAuditLog: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/governance/audit-log${q ? `?${q}` : ''}`, { headers: getAuthHeader() });
  },

  // ── Platform Owner (super admin) ──
  platformStats: () => axios.get(`${API}/platform/stats`, { headers: getAuthHeader() }),
  platformOrgs: () => axios.get(`${API}/platform/orgs`, { headers: getAuthHeader() }),
  platformOrgDetail: (orgId) => axios.get(`${API}/platform/orgs/${orgId}`, { headers: getAuthHeader() }),
  platformSuspendOrg: (orgId, reason) => axios.post(`${API}/platform/orgs/${orgId}/suspend`, { reason }, { headers: getAuthHeader() }),
  platformActivateOrg: (orgId) => axios.post(`${API}/platform/orgs/${orgId}/activate`, {}, { headers: getAuthHeader() }),
  platformPlans: () => axios.get(`${API}/platform/plans`, { headers: getAuthHeader() }),
  platformCreatePlan: (body) => axios.post(`${API}/platform/plans`, body, { headers: getAuthHeader() }),
  platformUpdatePlan: (planId, body) => axios.put(`${API}/platform/plans/${planId}`, body, { headers: getAuthHeader() }),
  platformArchivePlan: (planId) => axios.delete(`${API}/platform/plans/${planId}`, { headers: getAuthHeader() }),
  platformSubscriptions: () => axios.get(`${API}/platform/subscriptions`, { headers: getAuthHeader() }),
  platformAssignSubscription: (body) => axios.post(`${API}/platform/subscriptions`, body, { headers: getAuthHeader() }),
  platformUpdateSubscription: (subId, body) => axios.put(`${API}/platform/subscriptions/${subId}`, body, { headers: getAuthHeader() }),
  platformTemplates: () => axios.get(`${API}/platform/permission-templates`, { headers: getAuthHeader() }),
  platformCreateTemplate: (body) => axios.post(`${API}/platform/permission-templates`, body, { headers: getAuthHeader() }),
  platformAuditLog: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return axios.get(`${API}/platform/audit-log${q ? `?${q}` : ''}`, { headers: getAuthHeader() });
  },

  // ── Portal access (multi-portal auth · magic link · OTP · org create) ──
  createOrgWithOwner: (body) => axios.post(`${API}/platform/orgs/create`, body, { headers: getAuthHeader() }),
  platformModules: () => axios.get(`${API}/platform/modules`, { headers: getAuthHeader() }),
  platformOrgUsers: (orgId) => axios.get(`${API}/platform/orgs/${orgId}/users`, { headers: getAuthHeader() }),
  platformCreateOrgUser: (orgId, body) => axios.post(`${API}/platform/orgs/${orgId}/users`, body, { headers: getAuthHeader() }),
  platformApplyTemplate: (orgId, userId, body) => axios.post(`${API}/platform/orgs/${orgId}/users/${userId}/template`, body, { headers: getAuthHeader() }),
  platformSetUserPerms: (orgId, userId, body) => axios.post(`${API}/platform/orgs/${orgId}/users/${userId}/permissions`, body, { headers: getAuthHeader() }),
  platformRemoveUser: (orgId, userId) => axios.delete(`${API}/platform/orgs/${orgId}/users/${userId}`, { headers: getAuthHeader() }),
  platformResendInvite: (orgId, userId) => axios.post(`${API}/platform/orgs/${orgId}/users/${userId}/invite`, {}, { headers: getAuthHeader() }),
  platformResetUserPassword: (orgId, userId, body = {}) => axios.post(`${API}/platform/orgs/${orgId}/users/${userId}/password`, body, { headers: getAuthHeader() }),
  changeMyPassword: (body) => axios.post(`${API}/auth/change-password`, body, { headers: getAuthHeader() }),
  requestAccessLink: (body) => axios.post(`${API}/auth/access-link/request`, body),
  redeemAccessLink: (token) => axios.post(`${API}/auth/access-link/redeem`, { token }),
  requestOtp: (body) => axios.post(`${API}/auth/otp/request`, body),
  verifyOtp: (body) => axios.post(`${API}/auth/otp/verify`, body),
  portalRedirect: () => axios.get(`${API}/auth/portal-redirect`, { headers: getAuthHeader() }),

  // ── Org branding ──
  getMyOrg: () => axios.get(`${API}/org/me`, { headers: getAuthHeader() }),
  updateBranding: (body) => axios.put(`${API}/org/branding`, body, { headers: getAuthHeader() }),
  publicBranding: (orgId) => axios.get(`${API}/org/branding/public/${orgId}`),

  // ── Operational Readiness Engine ──
  readinessTemplates: () => axios.get(`${API}/readiness/templates`, { headers: getAuthHeader() }),
  readinessAssessments: () => axios.get(`${API}/readiness/assessments`, { headers: getAuthHeader() }),
  readinessCreate: (body) => axios.post(`${API}/readiness/assessments`, body, { headers: getAuthHeader() }),
  readinessGet: (id) => axios.get(`${API}/readiness/assessments/${id}`, { headers: getAuthHeader() }),
  readinessAnswer: (id, body) => axios.put(`${API}/readiness/assessments/${id}/answer`, body, { headers: getAuthHeader() }),
  readinessRecommend: (id) => axios.post(`${API}/readiness/assessments/${id}/recommend`, {}, { headers: getAuthHeader() }),
  readinessDashboard: () => axios.get(`${API}/readiness/dashboard`, { headers: getAuthHeader() }),
  readinessBenchmark: () => axios.get(`${API}/readiness/benchmark`, { headers: getAuthHeader() }),

  // ── Tenant Onboarding & Experience ──
  onboardingList: () => axios.get(`${API}/tenant-onboarding`, { headers: getAuthHeader() }),
  onboardingCreate: (body) => axios.post(`${API}/tenant-onboarding`, body, { headers: getAuthHeader() }),
  onboardingGet: (id) => axios.get(`${API}/tenant-onboarding/${id}`, { headers: getAuthHeader() }),
  onboardingUpdateTask: (id, taskId, body) => axios.put(`${API}/tenant-onboarding/${id}/task/${taskId}`, body, { headers: getAuthHeader() }),
  onboardingAcknowledge: (id, body) => axios.post(`${API}/tenant-onboarding/${id}/acknowledge`, body, { headers: getAuthHeader() }),
  onboardingMine: () => axios.get(`${API}/tenant-onboarding/me`, { headers: getAuthHeader() }),

  // ── CRM / Demo requests ──
  submitDemoRequest: (body) => axios.post(`${API}/demo-request`, body),

  // ── Saudi Real Estate Intelligence ──
  intelligenceValuation: (body) => axios.post(`${API}/intelligence/valuation`, body, { headers: getAuthHeader() }),
  intelligenceRentalPricing: (body) => axios.post(`${API}/intelligence/rental-pricing`, body, { headers: getAuthHeader() }),
  intelligenceComparables: (body) => axios.post(`${API}/intelligence/comparables`, body, { headers: getAuthHeader() }),
  intelligenceOpportunities: () => axios.get(`${API}/intelligence/opportunities`, { headers: getAuthHeader() }),
  intelligenceMarket: () => axios.get(`${API}/intelligence/market/dashboard`, { headers: getAuthHeader() }),
  intelligenceDistricts: () => axios.get(`${API}/intelligence/market/districts`, { headers: getAuthHeader() }),
  intelligencePipelineStatus: () => axios.get(`${API}/intelligence/data-pipeline/status`, { headers: getAuthHeader() }),

  // ── Sandbox / Demo isolation ──
  sandboxCreate: (body = {}) => axios.post(`${API}/sandbox/create`, body),
  sandboxMe: () => axios.get(`${API}/sandbox/me`, { headers: getAuthHeader() }),
  sandboxReset: () => axios.post(`${API}/sandbox/reset`, {}, { headers: getAuthHeader() }),
  sandboxDestroy: () => axios.post(`${API}/sandbox/destroy`, {}, { headers: getAuthHeader() }),
  sandboxCleanupExpired: () => axios.post(`${API}/sandbox/cleanup-expired`, {}, { headers: getAuthHeader() })
};